/**
 * O que faz: busca campanha, conjunto e anúncio Meta dos leads que já estão na lista de vendas.
 * Onde usa: filtros do painel de vendas.
 * Conexões: RPC listar_origens_anuncio_vendas e validação Zod.
 * Tabelas: leitura indireta de lead_ad_attributions, só nomes já identificados.
 * Data: 24/09/2026.
 */
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import type { OrigemAnuncioVenda } from '@/lib/vendas/filtrarLancamentosVendas';

const linhaCatalogoSchema = z.object({
  tipo: z.enum(['anuncio', 'conjunto', 'campanha']),
  nome: z.string(),
});

export interface CatalogoAnunciosVendas {
  anuncios: string[];
  conjuntos: string[];
  campanhas: string[];
}

export async function buscarCatalogoAnunciosVendas(clinicaId: string, signal?: AbortSignal): Promise<CatalogoAnunciosVendas> {
  // RPC listar_catalogo_anuncios_vendas: SELECT dos nomes distintos da clínica.
  // Campos: tipo (anúncio, conjunto ou campanha) e nome. Sem identificadores de clique.
  // Filtro: somente a clínica autorizada, até 300 nomes de cada tipo.
  const { data, error } = await supabase.rpc('listar_catalogo_anuncios_vendas' as never, {
    p_clinica_id: clinicaId,
  } as never).abortSignal(signal);

  if (error) throw new Error('Não foi possível carregar anúncios, conjuntos e campanhas.');
  const linhas = z.array(linhaCatalogoSchema).safeParse(data ?? []);
  if (!linhas.success) throw new Error('A lista de anúncios veio incompleta.');

  const catalogo: CatalogoAnunciosVendas = { anuncios: [], conjuntos: [], campanhas: [] };
  linhas.data.forEach((linha) => {
    if (linha.tipo === 'anuncio') catalogo.anuncios.push(linha.nome);
    if (linha.tipo === 'conjunto') catalogo.conjuntos.push(linha.nome);
    if (linha.tipo === 'campanha') catalogo.campanhas.push(linha.nome);
  });
  return catalogo;
}

const linhaSchema = z.object({
  lead_id: z.string().uuid(),
  campanha: z.string().nullable(),
  conjunto: z.string().nullable(),
  anuncio_meta: z.string().nullable(),
});

const TAMANHO_LOTE = 400;
// Limite de chamadas simultâneas à RPC (equilíbrio entre velocidade e carga no banco).
const LOTES_EM_PARALELO = 3;

export async function buscarOrigensAnuncioVendas(
  clinicaId: string,
  leadIds: string[],
  signal?: AbortSignal,
) {
  const mapa = new Map<string, OrigemAnuncioVenda>();
  const ids = [...new Set(leadIds)];
  const lotes: string[][] = [];
  for (let inicio = 0; inicio < ids.length; inicio += TAMANHO_LOTE) lotes.push(ids.slice(inicio, inicio + TAMANHO_LOTE));

  const buscarLote = async (lote: string[]) => {
    // RPC listar_origens_anuncio_vendas: SELECT do último toque identificado por lead.
    // Campos: lead_id, campanha, conjunto e nome do anúncio Meta. Sem click ID nem payload.
    // Filtros: clínica autorizada e somente os leads desta lista, em lotes de até 400.
    const { data, error } = await supabase.rpc('listar_origens_anuncio_vendas' as never, {
      p_clinica_id: clinicaId,
      p_lead_ids: lote,
    } as never).abortSignal(signal);

    if (error) throw new Error('Não foi possível carregar campanha e conjunto dos anúncios.');
    const linhas = z.array(linhaSchema).safeParse(data ?? []);
    if (!linhas.success) throw new Error('Os dados de campanha e conjunto vieram incompletos.');
    return linhas.data;
  };

  // Até 3 lotes ao mesmo tempo: períodos longos (milhares de leads) carregam ~3x mais rápido
  // sem disparar dezenas de chamadas simultâneas contra o banco. Se um lote falhar, o resultado
  // inteiro falha (Promise.all), para nunca mostrar campanha/conjunto pela metade.
  for (let inicio = 0; inicio < lotes.length; inicio += LOTES_EM_PARALELO) {
    const resultados = await Promise.all(lotes.slice(inicio, inicio + LOTES_EM_PARALELO).map(buscarLote));
    resultados.flat().forEach((linha) => {
      mapa.set(linha.lead_id, {
        campanha: linha.campanha,
        conjunto: linha.conjunto,
        anuncioMeta: linha.anuncio_meta,
      });
    });
  }
  return mapa;
}
